// PublicTeachers.js
import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Box,
  Avatar,
  InputAdornment,
  Chip,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Divider,
  Collapse,
} from "@mui/material";
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  setDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate, Link } from "react-router-dom";
import InfoDrawer from "./InfoDrawer";

import { useTheme } from "@mui/material/styles";
import { getStyles } from "../index";
import EditIcon from "@mui/icons-material/Edit";

import { formatUpvotes, flagUrls } from "./functions";
import AddIcon from "@mui/icons-material/Add";

import SchoolIcon from "@mui/icons-material/School";

import Logger from "./Logger";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import CategoryCarousel from "./CategoryCarousel";

const Languages = ({ user }) => {
  const [publicTeachers, setPublicTeachers] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryNames, setCategoryNames] = useState({});
  const [visibleTeachers, setVisibleTeachers] = useState({});

  const [loading, setLoading] = useState(false);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);
  const navigate = useNavigate();

  const theme = useTheme();
  const styles = getStyles(theme);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [categoryDescriptions, setCategoryDescriptions] = useState({});

  const [excludedTeacherIds] = useState([]);

  const excludedCategories = [""];

  const adminIds = [
    "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
    "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
    "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
    "2kBIBu71ATTVWFTKIunRrQndZM62",
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingTeachers(true);
      try {
        // Define priority categories here instead of using state
        const priorityCategoryIds = [
          "JSIpsfgRETwQnTrqkZTk",
          "xGEcygU8I6j5SJdF3ELR",
          "Jxzkjwml8juCP6xfU36t",
          "79KCCqmDuI9swIshYIZJ",
          "EDAOp3txNnFI6unyiTIF",
        ];

        // 1. Fetch teachers with optimized query
        const teachersRef = collection(db, "publicTeachers");
        const teachersQuery = query(
          teachersRef,
          where("userId", "in", adminIds),
          where("category", "==", "xGEcygU8I6j5SJdF3ELR"),
        );

        const teachersSnapshot = await getDocs(teachersQuery);

        // Apply client-side filters
        const teachersList = teachersSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((teacher) => !excludedTeacherIds.includes(teacher.id))
          .filter((teacher) => teacher.category !== "Galna karaktärer");

        setPublicTeachers(teachersList);

        // 2. Process categories
        const uniqueCategories = [
          ...new Set(
            teachersList.map((teacher) => teacher.category || "Övrigt"),
          ),
        ];

        // Sort categories with priority ones first
        const sortedCategories = [
          ...priorityCategoryIds.filter((id) => uniqueCategories.includes(id)),
          ...uniqueCategories.filter(
            (category) => !priorityCategoryIds.includes(category),
          ),
        ];

        setCategories(sortedCategories);

        // 3. Fetch category details in a single batch
        const categoryNamesMap = {};
        const categoryDescriptionsMap = {};

        // Only fetch category details for IDs that look like Firestore IDs
        const categoryIds = uniqueCategories.filter(
          (category) => typeof category === "string" && category.length === 20,
        );

        // Fetch all relevant categories in parallel
        const categoryPromises = categoryIds.map((categoryId) =>
          getDoc(doc(db, "categories", categoryId)),
        );

        const categoryDocs = await Promise.all(categoryPromises);

        categoryDocs.forEach((categoryDoc) => {
          if (categoryDoc.exists()) {
            const categoryData = categoryDoc.data();
            categoryNamesMap[categoryDoc.id] = categoryData.name;
            categoryDescriptionsMap[categoryDoc.id] = categoryData.description;
          }
        });

        setCategoryNames(categoryNamesMap);
        setCategoryDescriptions(categoryDescriptionsMap);

        // 4. Initialize visible teachers
        const initialVisibleTeachers = {};
        uniqueCategories.forEach((category) => {
          initialVisibleTeachers[category] = 6;
        });

        setVisibleTeachers(initialVisibleTeachers);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoadingTeachers(false);
      }
    };

    fetchData();
  }, []);
  const getCategoryName = (category) => {
    if (typeof category === "string" && category.length === 20) {
      return categoryNames[category] || "Unknown Category";
    }
    return category;
  };

  const filteredTeachers = publicTeachers.filter(
    (teacher) =>
      (teacher?.characterName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
        teacher?.tagline?.toLowerCase().includes(searchTerm.toLowerCase())) ??
      false,
  );

  const handleLoadMore = (category) => {
    setLoading(true);
    setTimeout(() => {
      setVisibleTeachers((prev) => ({
        ...prev,
        [category]: prev[category] + 6,
      }));
      setLoading(false);
    }, 500); // Simulating network delay
  };

  const getCategoryDescription = (category) => {
    if (typeof category === "string" && category.length === 20) {
      if (categoryDescriptions[category]) {
        return `${categoryDescriptions[category]}`;
      }
      return "Utforska spännande ämnen med våra erfarna lärare.";
    }
  };

  const languageMap = {
    french: "Franska",
    spanish: "Spanska",
    german: "Tyska",
    swedish: "SFI-Lärare",
    english: "Engelska",
    arabic: "Arabiska",
  };

  if (isLoadingTeachers) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{ ...styles.container, py: 0, width: { sm: `calc(100% - 400px)` } }}
      >
        <Box
          sx={{
            ...styles.innerContainer,
            width: { xs: "100%", md: "100%" },
            margin: "0 auto",
          }}
        >
          {/* Rendera kategorierna och dess lärare */}
          {categories
            .filter(
              (category) =>
                !excludedCategories.includes(getCategoryName(category)),
            )
            .map((category) => (
              <Box key={category} sx={{ marginBottom: 10 }}>
                <Typography
                  variant="h1"
                  sx={{ ...styles.header, borderBottom: "0px", mb: 6 }}
                >
                  {getCategoryName(category)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#8b9099", mb: 2, mt: 0.5, display: "none" }}
                >
                  {getCategoryDescription(category)}
                </Typography>
                <Grid container spacing={2}>
                  {filteredTeachers
                    .filter((teacher) => teacher.category === category)
                    .slice(0, visibleTeachers[category])
                    .map((teacher) => (
                      <Grid item xs={12} sm={6} md={6} key={teacher.id}>
                        <Link
                          to={`/app/chat/${teacher.id}`}
                          style={{ textDecoration: "none" }}
                        >
                          <Card
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              borderRadius: "16px",
                              overflow: "hidden",

                              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                              border: "none",
                              position: "relative",
                              background:
                                theme.palette.mode === "dark"
                                  ? "#1d2b37"
                                  : "#fff",
                              color:
                                theme.palette.mode === "dark" ? "#fff" : "#444",
                              transition:
                                "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                              "&:hover": {
                                transform: "translateY(-5px)",
                              },
                            }}
                          >
                            <CardContent
                              sx={{
                                position: "relative",
                                padding: "16px !important",
                              }}
                            >
                              {user.admin && (
                                <IconButton
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(
                                      `/app/larare/${teacher.id}`,
                                      "_blank",
                                    );
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: "12px" }} />
                                </IconButton>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ position: "relative" }}>
                                    <Avatar
                                      src={teacher.imageUrl}
                                      alt={teacher.characterName}
                                      sx={{
                                        width: 100,
                                        height: 120,
                                        marginRight: 2,
                                        borderRadius: "16px",
                                        border: "2px solid white",
                                        boxShadow:
                                          theme.palette.mode === "dark"
                                            ? "0 2px 10px rgba(0,0,0,0.2)"
                                            : "none",
                                      }}
                                    />
                                    {flagUrls[teacher.languageToLearn] && (
                                      <Box
                                        component="img"
                                        src={flagUrls[teacher.languageToLearn]}
                                        alt={`${teacher.languageToLearn} `}
                                        sx={{
                                          position: "absolute",
                                          top: "-4px",
                                          right: "6px",
                                          width: "32px",
                                          height: "32px",
                                          borderRadius: "50%",
                                          border: "1px solid white",
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <Box sx={{ display: "block", mb: 0.7 }}>
                                      <Typography
                                        variant="h5"
                                        sx={{
                                          fontWeight: "600",
                                          fontSize: "1.3rem",
                                        }}
                                      >
                                        {teacher.languageToLearn
                                          ? languageMap[
                                              teacher.languageToLearn.toLowerCase()
                                            ] || teacher.languageToLearn
                                          : teacher.traditionalTeacherSubject
                                            ? teacher.traditionalTeacherSubject
                                            : teacher.characterName}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: "none" }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                          marginTop: "5px",
                                        }}
                                      >
                                        <ThumbUpOffAltIcon
                                          sx={{ fontSize: "14px" }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "13px",
                                            lineHeight: 1,
                                            fontWeight: "500",
                                          }}
                                        >
                                          {formatUpvotes(teacher.upvotes)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                                <IconButton sx={{ marginLeft: "auto" }}>
                                  <ChevronRightIcon />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </Card>
                        </Link>
                      </Grid>
                    ))}
                </Grid>
                {filteredTeachers.filter(
                  (teacher) => teacher.category === category,
                ).length > visibleTeachers[category] && (
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleLoadMore(category)}
                      sx={{ textTransform: "none", fontWeight: "bold" }}
                    >
                      Visa fler
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

          {!isLoadingTeachers && (
            <Box
              sx={{
                ...styles.bottomCta,
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <SchoolIcon
                sx={{
                  fontSize: { xs: 40, md: 60 },
                  color: theme.palette.primary.main,
                  marginBottom: 2,
                }}
              />
              <Typography variant="h4" sx={{ ...styles.bottomCtaTitle }}>
                Skapa en AI-assistent
              </Typography>
              <Typography variant="body1" sx={{ ...styles.bottomCtaBody }}>
                Designa en unik AI-karaktär som passar dina specifika behov.
                Anpassa kunskapsområden, personlighet och interaktionsstil för
                en skräddarsydd upplevelse.
              </Typography>
              <Button
                component={Link}
                to="/app/larare"
                variant={theme.palette.mode === "light" ? "purp" : "contained"}
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  fontSize: "1.1rem",
                  marginTop: "20px",
                }}
              >
                Skapa din egen AI-assistent
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <InfoDrawer
        permanentlyOpen={true}
        open={true}
        drawerType="languages"
        width="27%"
      />
    </>
  );
};

export default Languages;
